import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AddLaborService.css';

const initialForm = {
  jobgroup_id: '',
  job_items: '',
  price_car_4c_800_1600: '',
  price_mid_1600_2400: '',
  price_large_2500_3200: '',
  price_xlarge_3500_5700: '',
  duration_800_1600: '30',
  duration_1600_2400: '45',
  duration_2400_3400: '60',
  duration_3500_5700: '120',
};

function AddLaborService() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [jobgroups, setJobgroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    const fetchJobgroups = async () => {
      setLoadingGroups(true);
      setError('');

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/labor/jobgroups', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setJobgroups(response.data.jobgroups || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load job groups.');
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchJobgroups();
  }, []);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setStatus('');

    const jobItems = form.job_items
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    if (jobItems.length === 0) {
      setError('Write at least one job item.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/labor', {
        ...form,
        job_items: jobItems,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setStatus('Service saved successfully.');
      setTimeout(() => {
        navigate('/inventory/labor');
      }, 800);
    } catch (err) {
      setError(
        err.response?.data?.message ||
        (err.response?.data?.errors && err.response.data.errors[0]?.msg) ||
        'Failed to save service.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-labor-page">
      <button className="app-back-button" onClick={() => navigate('/inventory/labor')}>
        Back
      </button>

      <div className="add-labor-card">
        <form className="add-labor-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <label>Job Group</label>
            <select
              value={form.jobgroup_id}
              onChange={handleChange('jobgroup_id')}
              disabled={loading || loadingGroups}
              required
            >
              <option value="">{loadingGroups ? 'Loading job groups...' : 'Select job group'}</option>
              {jobgroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <label>Job Items</label>
            <textarea
              value={form.job_items}
              onChange={handleChange('job_items')}
              placeholder="Write one job item per line"
              rows="7"
              maxLength="4000"
              disabled={loading}
              required
            />
          </div>

          <div className="form-grid two-columns">
            <div className="form-row">
              <label>800-1600 Price</label>
              <input
                value={form.price_car_4c_800_1600}
                onChange={handleChange('price_car_4c_800_1600')}
                placeholder="Price"
                disabled={loading}
              />
            </div>
            <div className="form-row">
              <label>1600-2400 Price</label>
              <input
                value={form.price_mid_1600_2400}
                onChange={handleChange('price_mid_1600_2400')}
                placeholder="Price"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-grid two-columns">
            <div className="form-row">
              <label>2500-3200 Price</label>
              <input
                value={form.price_large_2500_3200}
                onChange={handleChange('price_large_2500_3200')}
                placeholder="Price"
                disabled={loading}
              />
            </div>
            <div className="form-row">
              <label>3500-5700 Price</label>
              <input
                value={form.price_xlarge_3500_5700}
                onChange={handleChange('price_xlarge_3500_5700')}
                placeholder="Price"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-grid four-columns">
            <div className="form-row">
              <label>800-1600 Min</label>
              <input
                type="number"
                min="0"
                value={form.duration_800_1600}
                onChange={handleChange('duration_800_1600')}
                disabled={loading}
              />
            </div>
            <div className="form-row">
              <label>1600-2400 Min</label>
              <input
                type="number"
                min="0"
                value={form.duration_1600_2400}
                onChange={handleChange('duration_1600_2400')}
                disabled={loading}
              />
            </div>
            <div className="form-row">
              <label>2400-3400 Min</label>
              <input
                type="number"
                min="0"
                value={form.duration_2400_3400}
                onChange={handleChange('duration_2400_3400')}
                disabled={loading}
              />
            </div>
            <div className="form-row">
              <label>3500-5700 Min</label>
              <input
                type="number"
                min="0"
                value={form.duration_3500_5700}
                onChange={handleChange('duration_3500_5700')}
                disabled={loading}
              />
            </div>
          </div>

          {error && <div className="labor-form-error">{error}</div>}
          {status && <div className="labor-form-success">{status}</div>}

          <div className="add-labor-actions">
            <button type="submit" className="save-service-btn" disabled={loading || loadingGroups}>
              {loading ? 'Saving...' : 'Save Service'}
            </button>
            <button
              type="button"
              className="cancel-service-btn"
              onClick={() => navigate('/inventory/labor')}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddLaborService;
